import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../App';

function EventScheduler() {
  const { T, language } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    topic: '',
    date: '',
    startTime: '',
    endTime: '',
    frequency: 'oneTime',
    location: '',
    description: '',
    inviteMembers: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleClearChanges = () => {
    setFormData({
      topic: '',
      date: '',
      startTime: '',
      endTime: '',
      frequency: 'oneTime',
      location: '',
      description: '',
      inviteMembers: ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Event scheduled:', formData);
    // Here you would typically send the data to your API
    navigate('/event-map');
  };

  const handleSendInvite = () => {
    console.log('Sending invite for:', formData);
    // Here you would typically send invites
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-emerald-600">{language === 'en' ? 'Schedule a Task' : 'タスクをスケジュール'}</h2>
        <button
          type="button"
          onClick={handleClearChanges}
          className="px-4 py-2 text-gray-600 hover:text-gray-800 transition duration-200"
        >
          {T.scheduler.clearChanges}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Topic */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              {language === 'en' ? 'Task Title' : 'タスクタイトル'}
            </label>
            <input
              type="text"
              name="topic"
              value={formData.topic}
              onChange={handleInputChange}
              placeholder={language === 'en' ? 'Enter task title...' : 'タスクタイトルを入力...'}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              required
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              {language === 'en' ? 'Due Date' : '期限日'}
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              required
            />
          </div>

          {/* Start Time */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              {language === 'en' ? 'Start Time' : '開始時間'}
            </label>
            <input
              type="time"
              name="startTime"
              value={formData.startTime}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              required
            />
          </div>

          {/* End Time */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              {language === 'en' ? 'End Time' : '終了時間'}
            </label>
            <input
              type="time"
              name="endTime"
              value={formData.endTime}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              required
            />
          </div>
        </div>

        {/* Frequency */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">
            {language === 'en' ? 'Frequency' : '頻度'}
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="frequency"
                value="oneTime"
                checked={formData.frequency === 'oneTime'}
                onChange={handleInputChange}
                className="mr-2"
              />
              {T.scheduler.oneTime}
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="frequency"
                value="weekly"
                checked={formData.frequency === 'weekly'}
                onChange={handleInputChange}
                className="mr-2"
              />
              {T.scheduler.weekly}
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="frequency"
                value="monthly"
                checked={formData.frequency === 'monthly'}
                onChange={handleInputChange}
                className="mr-2"
              />
              {T.scheduler.monthly}
            </label>
          </div>
        </div>

        {/* Location */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">
            {language === 'en' ? 'Location' : '場所'}
          </label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            placeholder={language === 'en' ? 'Enter location or "Remote"...' : '場所または「リモート」を入力...'}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            required
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">
            {language === 'en' ? 'Task Description' : 'タスクの説明'}
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder={language === 'en' ? 'Describe the task details...' : 'タスクの詳細を説明...'}
            rows="4"
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>

        {/* Invite Members */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">
            {language === 'en' ? 'Assign to Team Members' : 'チームメンバーに割り当て'}
          </label>
          <div className="flex space-x-2">
            <input
              type="text"
              name="inviteMembers"
              value={formData.inviteMembers}
              onChange={handleInputChange}
              placeholder={language === 'en' ? 'Enter member names or emails...' : 'メンバー名またはメールを入力...'}
              className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <button
              type="button"
              onClick={handleSendInvite}
              className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-lg transition duration-200"
            >
              {language === 'en' ? 'Assign Task' : 'タスク割り当て'}
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/event-map')}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold rounded-lg transition duration-200"
          >
            {language === 'en' ? 'Cancel' : 'キャンセル'}
          </button>
          <button
            type="submit"
            className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg transition duration-200"
          >
            {language === 'en' ? 'Create Task' : 'タスク作成'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EventScheduler;
